/**
 * DATA SYNC SERVICE - PRODUCTION
 * 
 * Syncs all app data to/from Supabase via server-side API.
 * NO localStorage dependencies - all data from database.
 */

import { apiUrl } from '../config/api';
import { supabase } from '../lib/supabase';

// ============================================================================
// HELPER: Get current user ID from Supabase auth
// ============================================================================

export const getCurrentUserId = async () => {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        return session?.user?.id || null;
    } catch (error) {
        console.error('[DataSync] Error getting user ID:', error);
        return null;
    }
};

// ============================================================================
// API CALL HELPER
// ============================================================================

const saveToServer = async (type, data, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();

    if (!userIdToUse) {
        console.warn('[DataSync] No user ID available');
        return { success: false, error: 'No user ID' };
    }

    try {
        console.log(`[DataSync] Saving ${type} via API for user:`, userIdToUse);

        const response = await fetch(apiUrl('/api/save-user-data'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type, userId: userIdToUse, data })
        });

        const result = await response.json();

        if (!response.ok || !result.success) {
            console.error(`[DataSync] ${type} save failed:`, result.error);
            return { success: false, error: result.error };
        }

        console.log(`✅ [DataSync] ${type} saved to Supabase`);
        return { success: true };
    } catch (err) {
        console.error(`[DataSync] ${type} sync error:`, err);
        return { success: false, error: err.message };
    }
};

// ============================================================================
// PROFILE SYNC
// ============================================================================

export const syncProfileToSupabase = async (profileData) => {
    return saveToServer('profile', profileData);
};

export const loadProfileFromSupabase = async (userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return null;

    try {
        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', userIdToUse)
            .single();

        if (error) throw error;
        return data;
    } catch (err) {
        console.error('[DataSync] Failed to load profile:', err);
        return null;
    }
};

// ============================================================================
// MEAL LOGS SYNC
// ============================================================================

export const syncMealToSupabase = async (meal) => {
    return saveToServer('meal', meal);
};

export const loadMealsFromSupabase = async (date = null, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return [];

    try {
        let query = supabase
            .from('meal_logs')
            .select('*')
            .eq('user_id', userIdToUse)
            .order('logged_at', { ascending: false });

        if (date) {
            const startOfDay = new Date(date);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(date);
            endOfDay.setHours(23, 59, 59, 999);

            query = query
                .gte('logged_at', startOfDay.toISOString())
                .lte('logged_at', endOfDay.toISOString());
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error('[DataSync] Failed to load meals:', err);
        return [];
    }
};

/**
 * Update an existing meal in Supabase
 * @param {string} mealId - The meal ID to update
 * @param {Object} updatedData - The updated meal data
 * @returns {Object} { success: boolean, error?: string }
 */
export const updateMealInSupabase = async (mealId, updatedData, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) {
        console.warn('[DataSync] No user ID available for meal update');
        return { success: false, error: 'No user ID' };
    }

    try {
        console.log(`[DataSync] Updating meal ${mealId}`);

        const { error } = await supabase
            .from('meal_logs')
            .update({
                ...updatedData,
                updated_at: new Date().toISOString()
            })
            .eq('id', mealId)
            .eq('user_id', userIdToUse);

        if (error) throw error;

        console.log(`✅ [DataSync] Meal ${mealId} updated in Supabase`);
        return { success: true };
    } catch (err) {
        console.error('[DataSync] Failed to update meal:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Soft delete a meal from Supabase (sets is_deleted flag)
 * @param {string} mealId - The meal ID to delete
 * @returns {Object} { success: boolean, error?: string }
 */
export const deleteMealFromSupabase = async (mealId, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) {
        console.warn('[DataSync] No user ID available for meal delete');
        return { success: false, error: 'No user ID' };
    }

    try {
        console.log(`[DataSync] Deleting meal ${mealId}`);

        // Soft delete - set is_deleted flag
        const { error } = await supabase
            .from('meal_logs')
            .update({
                is_deleted: true,
                deleted_at: new Date().toISOString()
            })
            .eq('id', mealId)
            .eq('user_id', userIdToUse);

        if (error) throw error;

        console.log(`✅ [DataSync] Meal ${mealId} deleted from Supabase`);
        return { success: true };
    } catch (err) {
        console.error('[DataSync] Failed to delete meal:', err);
        return { success: false, error: err.message };
    }
};

// ============================================================================
// WORKOUT LOGS SYNC
// ============================================================================

export const syncWorkoutToSupabase = async (exercise) => {
    return saveToServer('workout', exercise);
};

export const loadWorkoutsFromSupabase = async (date = null, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return [];

    try {
        let query = supabase
            .from('workout_logs')
            .select('*')
            .eq('user_id', userIdToUse)
            .order('logged_at', { ascending: false });

        if (date) {
            const startOfDay = new Date(date);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(date);
            endOfDay.setHours(23, 59, 59, 999);

            query = query
                .gte('logged_at', startOfDay.toISOString())
                .lte('logged_at', endOfDay.toISOString());
        }

        const { data, error } = await query;
        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error('[DataSync] Failed to load workouts:', err);
        return [];
    }
};

/**
 * Update an existing workout in Supabase
 * @param {string} workoutId - The workout ID to update
 * @param {Object} updatedData - The updated workout data
 * @returns {Object} { success: boolean, error?: string }
 */
export const updateWorkoutInSupabase = async (workoutId, updatedData, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) {
        console.warn('[DataSync] No user ID available for workout update');
        return { success: false, error: 'No user ID' };
    }

    try {
        console.log(`[DataSync] Updating workout ${workoutId}`);

        const { error } = await supabase
            .from('workout_logs')
            .update({
                ...updatedData,
                updated_at: new Date().toISOString()
            })
            .eq('id', workoutId)
            .eq('user_id', userIdToUse);

        if (error) throw error;

        console.log(`✅ [DataSync] Workout ${workoutId} updated in Supabase`);
        return { success: true };
    } catch (err) {
        console.error('[DataSync] Failed to update workout:', err);
        return { success: false, error: err.message };
    }
};

/**
 * Soft delete a workout from Supabase (sets is_deleted flag)
 * @param {string} workoutId - The workout ID to delete
 * @returns {Object} { success: boolean, error?: string }
 */
export const deleteWorkoutFromSupabase = async (workoutId, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) {
        console.warn('[DataSync] No user ID available for workout delete');
        return { success: false, error: 'No user ID' };
    }

    try {
        console.log(`[DataSync] Deleting workout ${workoutId}`);

        // Soft delete - set is_deleted flag
        const { error } = await supabase
            .from('workout_logs')
            .update({
                is_deleted: true,
                deleted_at: new Date().toISOString()
            })
            .eq('id', workoutId)
            .eq('user_id', userIdToUse);

        if (error) throw error;

        console.log(`✅ [DataSync] Workout ${workoutId} deleted from Supabase`);
        return { success: true };
    } catch (err) {
        console.error('[DataSync] Failed to delete workout:', err);
        return { success: false, error: err.message };
    }
};

// ============================================================================
// WEIGHT LOGS SYNC
// ============================================================================

export const syncWeightToSupabase = async (weight, notes = null) => {
    return saveToServer('weight', { weight, notes });
};

export const loadWeightHistoryFromSupabase = async (limit = 30, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return [];

    try {
        const { data, error } = await supabase
            .from('weight_logs')
            .select('*')
            .eq('user_id', userIdToUse)
            .order('logged_at', { ascending: false })
            .limit(limit);

        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error('[DataSync] Failed to load weight history:', err);
        return [];
    }
};

// ============================================================================
// ROUTINES SYNC
// ============================================================================

export const syncRoutineToSupabase = async (routine) => {
    return saveToServer('routine', routine);
};

export const loadRoutinesFromSupabase = async (userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return [];

    try {
        const { data, error } = await supabase
            .from('routines')
            .select('*')
            .eq('user_id', userIdToUse)
            .order('created_at', { ascending: false });

        if (error) throw error;
        return data || [];
    } catch (err) {
        console.error('[DataSync] Failed to load routines:', err);
        return [];
    }
};

export const deleteRoutineFromSupabase = async (routineId, userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) return { success: false, error: 'Not logged in' };

    try {
        const { error } = await supabase
            .from('routines')
            .delete()
            .eq('id', routineId)
            .eq('user_id', userIdToUse);

        if (error) throw error;
        return { success: true };
    } catch (err) {
        console.error('[DataSync] Failed to delete routine:', err);
        return { success: false, error: err.message };
    }
};

// ============================================================================
// FAVORITES SYNC
// ============================================================================

export const syncSavedMealToSupabase = async (meal) => {
    return saveToServer('saved_meal', meal);
};

export const syncSavedExerciseToSupabase = async (exercise) => {
    return saveToServer('saved_exercise', exercise);
};

// ============================================================================
// BULK SYNC - Loads all from Supabase
// ============================================================================

export const loadAllFromSupabase = async (userId = null) => {
    const userIdToUse = userId || await getCurrentUserId();
    if (!userIdToUse) {
        return { profile: null, meals: [], workouts: [], routines: [] };
    }

    const [profile, meals, workouts, routines] = await Promise.all([
        loadProfileFromSupabase(userIdToUse),
        loadMealsFromSupabase(null, userIdToUse),
        loadWorkoutsFromSupabase(null, userIdToUse),
        loadRoutinesFromSupabase(userIdToUse)
    ]);

    return { profile, meals, workouts, routines };
};

export default {
    getCurrentUserId,
    syncProfileToSupabase,
    loadProfileFromSupabase,
    syncMealToSupabase,
    loadMealsFromSupabase,
    updateMealInSupabase,
    deleteMealFromSupabase,
    syncWorkoutToSupabase,
    loadWorkoutsFromSupabase,
    updateWorkoutInSupabase,
    deleteWorkoutFromSupabase,
    syncWeightToSupabase,
    loadWeightHistoryFromSupabase,
    syncSavedMealToSupabase,
    syncSavedExerciseToSupabase,
    syncRoutineToSupabase,
    loadRoutinesFromSupabase,
    deleteRoutineFromSupabase,
    loadAllFromSupabase
};
